import { Component, Input, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import {
    BranchService,
    GalleryService,
    HorsePermission,
    Media,
    MediaDocumentType,
    MediaService,
    ToastService,
    UserToHorseSummary
} from '@caballus/ui-common';
import { UserState } from '@caballus/ui-state';
import { Share } from '@capacitor/share';
import { HorseCache } from '@ion-caballus/core/caches';
import { environment } from '@ion-caballus/env';
import { AlertController, ModalController } from '@ionic/angular';
import { Select } from '@ngxs/store';
import { BehaviorSubject, Observable, Subject, combineLatest, from, of } from 'rxjs';
import { map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { PromptMsgModalComponent } from '../prompt-msg-modal/prompt-msg-modal.component';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MediaType } from '@awesome-cordova-plugins/camera/ngx';
import { HttpClient } from '@angular/common/http';
import { Directory, Filesystem } from '@capacitor/filesystem';

const PLACEHOLDER_IMAGE_URL = '/assets/images/horse-placeholder.png';
const FULLSCREEN_EL_ID = 'fullscreenpicture';
const DISMISS_FULLSCREEN_FN = (): void => document.getElementById(FULLSCREEN_EL_ID).remove();
@Component({
    selector: 'app-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.scss']
})
export class ImageViewerComponent implements OnInit, OnDestroy {
    @Select(UserState.horseRelations)
    public horseRelations$: Observable<UserToHorseSummary[]>;
    public _showOnFullscreen$: Subject<void> = new Subject<void>();
    @Input() public attachmentUrl: string;
    public attachmentUrl$: BehaviorSubject<string | SafeResourceUrl> = new BehaviorSubject<
        string | SafeResourceUrl
    >(PLACEHOLDER_IMAGE_URL);
    public showFullImage = false;
    private _onDestroy$: Subject<void> = new Subject<void>();

    @Input() public mediaId: string;

    @Input() public horseId: string;

    @Input() public pinRemoveView: boolean;

    @Input() public onlyView: boolean;

    @Input() public mediaType: MediaType;

    public canEdit$: Observable<boolean>;
    public urlSafe: SafeResourceUrl;
    constructor(
        private _modalCtrl: ModalController,
        private readonly _alertController: AlertController,
        private readonly _galleryService: GalleryService,
        private readonly _horseCache: HorseCache,
        private readonly _branchService: BranchService,
        private readonly _modalController: ModalController,
        private _domSanitizer: DomSanitizer,
        private _toastService: ToastService,
        private _http: HttpClient,
        private readonly _mediaSerice: MediaService
    ) {}

    ngOnInit(): void {
        this.canEdit$ = this.horseRelations$.pipe(
            take(1),
            map(relations => {
                const relation = relations
                    ? relations.find(r => r.horseIdentity._id === this.horseId)
                    : null;
                return relation
                    ? relation.horseRoleReference.permissions.includes(HorsePermission.HorseEdit)
                    : false;
            })
        );
        if (this.attachmentUrl) {
            this.attachmentUrl$.next(this.attachmentUrl);
        }

        if (this.mediaType == MediaType.VIDEO) {
            this.urlSafe = this.getJWplayerUrl(this.attachmentUrl);
            this.urlSafe = this._domSanitizer.sanitize(SecurityContext.RESOURCE_URL, this.urlSafe);
        } else {
            this.urlSafe = this.attachmentUrl;
        }

        this._showOnFullscreen$
            .pipe(
                takeUntil(this._onDestroy$),
                withLatestFrom(this.attachmentUrl$),
                tap(([_, url]) => {
                    const img: HTMLIonImgElement = document.createElement('ion-img');
                    img.id = FULLSCREEN_EL_ID;
                    img.src = url as string;
                    img.onclick = DISMISS_FULLSCREEN_FN;
                    img.style.position = 'fixed';
                    img.style.top = '0px';
                    img.style.bottom = '0px';
                    img.style.left = '0px';
                    img.style.right = '0px';
                    img.style.backgroundColor = 'gainsboro';
                    document.body.appendChild(img);
                })
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this._onDestroy$.next();
        this._onDestroy$.complete();
    }

    public dismiss(value?: any): void {
        this._modalCtrl.dismiss(value);
    }

    public async showAlert(): Promise<void> {
        const buttons = [
            {
                text: 'Delete Media from this App',
                handler: (): void => {
                    this.deleteGalleryMedia(this.mediaId);
                }
            },
            {
                text: 'Keep Image',
                role: 'cancel'
            }
        ];

        const alert = await this._alertController.create({
            header: 'Select Option',
            cssClass: 'four-button-alert',
            buttons: [...buttons]
        });

        await alert.present();
    }

    private _promptMsgModal(): Observable<string> {
        const promise = this._modalController.create({
            cssClass: 'prompt-msg-modal',
            component: PromptMsgModalComponent
        });
        return from(promise).pipe(
            take(1),
            tap(m => m.present()),
            switchMap(m => from(m.onDidDismiss())),
            map(detail => detail.data)
        );
    }

    public onShare(attachmentUrl: string): void {
        combineLatest([this._promptMsgModal(), this._horseCache.getHorseDetails(this.horseId)])
            .pipe(
                tap(async ([msg, horse]) => {
                    const route = `/tabs/horse-profile/detail-horse/general-tab/${horse._id}`;
                    const desktopUrl = `${environment.ionBaseUrl}/${route}`;
                    const description = [
                        `${msg && msg.trim().length ? msg : 'Photo taken with Caballus'}`
                    ].join('\n');
                    const title = `Photo with ${horse.profile.commonName}`;
                    let contentType = '';
                    this._mediaSerice
                        .getMediaById(this.mediaId)
                        .pipe(
                            switchMap((media: Media) => {
                                if (media.latest.type === MediaDocumentType.Image) {
                                    return from(
                                        fetch(media.latest.url).then(response => response.blob())
                                    ).pipe(
                                        map(blob => ({ blob, media })), // Pass media along with blob
                                        switchMap(({ blob, media }) => {
                                            const newblob = new Blob([blob], {
                                                type: 'image/jpeg'
                                            });
                                            const reader = new FileReader();
                                            reader.readAsDataURL(newblob);
                                            return new Promise<{ base64: string; media: Media }>(
                                                (resolve, reject) => {
                                                    // Adjust promise to resolve an object with base64 and media
                                                    reader.onloadend = () => {
                                                        resolve({
                                                            base64: (reader.result as string).split(
                                                                ','
                                                            )[1],
                                                            media
                                                        });
                                                    };
                                                }
                                            );
                                        }),
                                        switchMap(({ base64, media }) => {
                                            // Receive base64 and media

                                            async function saveBase64ToFile(
                                                base64: string,
                                                filename: string
                                            ): Promise<string> {
                                                const result = await Filesystem.writeFile({
                                                    path: filename + '.jpeg',
                                                    data: base64,
                                                    directory: Directory.Cache,
                                                    recursive: true // Creates directories as needed
                                                });

                                                return result.uri;
                                            }

                                            return from(
                                                saveBase64ToFile(base64, media.latest.name)
                                            );
                                        }),
                                        switchMap(blobUrl => {
                                            return Share.share({
                                                title,
                                                text: description,
                                                files: [blobUrl],
                                                dialogTitle: title
                                            });
                                        }),
                                        map(() => media)
                                    );
                                } else {
                                    return of(null);
                                }
                            })
                        )
                        .subscribe(val => {
                            //if we got back null (no image was available) share as url
                            if (!val) {
                                const shareUrl = `${environment.webserver}/horse/profile/share?id=${
                                    this.horseId
                                }&title=${encodeURIComponent(title)}&image=${encodeURIComponent(
                                    attachmentUrl
                                )}&description=${encodeURIComponent(
                                    description
                                )}&desktopUrl=${encodeURIComponent(desktopUrl)}`;
                                from(
                                    this._branchService.createLinkWithOpenGraph({
                                        url: shareUrl,
                                        appRoute: `/tabs/horse-profile/detail-horse/general-tab/${horse._id}`,
                                        title,
                                        description,
                                        imageUrl: attachmentUrl,
                                        feature: 'shareHorseProfile'
                                    })
                                ).subscribe(deeplink => {
                                    Share.share({
                                        title,
                                        text: description,
                                        url: deeplink,
                                        dialogTitle: title
                                    });
                                });
                            }
                        });
                })
            )
            .subscribe();
    }

    public toggleFullImage(): void {
        this._showOnFullscreen$.next();
    }

    public deleteGalleryMedia(mediaId): void {
        this._galleryService
            .deleteGalleryMedia({ horseId: this.horseId, mediaId: mediaId })
            .subscribe(() => {
                this.dismiss();
            });
    }

    public pinMedia(): void {
        this._galleryService
            .pinMedia({ horseId: this.horseId, mediaToPin: this.mediaId })
            .subscribe(() => {
                this.dismiss();
            });
    }

    public removePin(): void {
        this._galleryService
            .unpinMedia({ horseId: this.horseId, mediaToUnpinId: this.mediaId })
            .subscribe(() => {
                this.dismiss();
            });
    }
    public isVideoUrl(url: string): boolean {
        return url.includes('jwplatform');
    }
    public getJWplayerUrl(jwPlayerId) {
        return this._domSanitizer.bypassSecurityTrustResourceUrl(
            'https://content.jwplatform.com/players/' + jwPlayerId + '.html'
        );
    }
}
